import React, { FC, useState, useRef, useEffect, useMemo, memo } from 'react';
import noop from 'lodash/noop';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { usePrevious } from 'react-use';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import 'react-tippy/dist/tippy.css';

import { getLogger } from 'companion-app-components/utils/core';
import { authSelectors } from 'companion-app-components/flux/auth';
import type RootState from 'companion-app-components/utils/redux-store/rootState';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';
import { chartOfAccountsTypes } from 'companion-app-components/flux/chart-of-accounts';
import { datasetsSelectors, datasetsUtils } from 'companion-app-components/flux/datasets';
import { transactionsUtils, transactionsTypes } from 'companion-app-components/flux/transactions';
import { scheduledTransactionsSelectors } from 'companion-app-components/flux/scheduled-transactions';
import { accountColorForId as accountColorForIdSelector } from 'companion-app-components/flux/preferences/selectors';

import Dump from 'components/Dump';
import useQCurrency from 'components/QCurrency/useQCurrency';
import useQDialogs from 'components/QDialogs/useQDialogs';
import ClrField from 'components/QuickenControls/ClrField';
import SplitsAndDetailsPanel from 'components/SplitsAndDetailsPanel';
import { fieldIsEditable } from 'components/TransactionRegister/helpers';

import { getPayeesForAccounts } from 'data/payees/selectors';
import { isC2REnabledAccount } from 'data/accounts/utils';
import { getTransactionsByAccountId, isTransferWritableAccountAvailable } from 'data/transactions/selectors';
import { getTxnDifferences, isNextScheduledInstance,
  isUnacceptedScheduledTxn, isBankOwnedPending,
  isBankPendingTxn, txnCategorySign, 
} from 'data/transactions/utils';
import { hasDatasetWriteAccess } from 'data/datasetSharing/selectors';

import { isOldEdge } from 'utils/utils';

import SaveField from './components/SaveField';
import TagsField from './components/TagsField';
import MenuField from './components/MenuField';
import CloseField from './components/CloseField';
import SplitField from './components/SplitField';
import NotesField from './components/NotesField';
import CheckField from './components/CheckField';
import MatchField from './components/MatchField';
import PayeeField from './components/PayeeField';
import SelectField from './components/SelectField';
import ActionField from './components/ActionField';
import IgnoredField from './components/IgnoredField';
import AccountField from './components/AccountField';
import BalanceField from './components/BalanceField';
import ReviewedField from './components/ReviewedField';
import UserFlagField from './components/UserFlagField';
import PostedOnField from './components/PostedOnField';
import CategoryField from './components/CategoryField'; 
import AttachmentField from './components/AttachmentField';
import TxnRowAmountField from './components/TxnRowAmountField';
import DoubleAmountField from './components/DoubleAmountField';

import useRefChangeWatcher from './useRefChangeWatcher';
import { getColorClass, getCurrencySymbol, QUEUE_PAYMENTS } from './utils';
import { TransactionRowProps, AttributesType } from './types';
import { RegTxList, RegTxListRow, RegListItem, styles } from './styles';

const log = getLogger('components/TransactionRegister/TransactionRow/index.tsx');
const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line

const useStyles = makeStyles()(styles as Record<string, any>);

const SHOW_ALL = true;
const msgGrossPaycheck = "Paycheck can't be edited.";

const TransactionRow: FC<TransactionRowProps> = (props) => {

  const {   
    id,
    txn,
    flashTx,
    qcardId,
    longCats,
    wrapText,
    scriptId,
    calendar,
    callbacks,
    wrapperId,
    accountIds,
    simpleEdit,
    headerFields,
    selectedTxns,
    doubleAmounts,
    allAccountIds,
    editDownloaded,
    showSplitWindow,
    registerComfort,
    currEditFieldName,
    showAccountColors,
    regEditableFields,
    showCurrencySymbol,
    shade = false,
    lastRow = false,
    editable = false,
    showBalance = true,
    showAccounts = false,
    refFn,
    onRowSelect,
    sizesChanged,
    onFocusField,
    isTxnDirty = false,
    showNewTxn = false,
  } = props;

  const { classes } = useStyles();
  const { dialogAlert } = useQDialogs();
  const { getTxnCurrency } = useQCurrency();

  const datasetId = useSelector((state: RootState) => authSelectors.getDatasetId(state));
  const payeeCategoryMap = useSelector((state: RootState) => getPayeesForAccounts(state, props));
  const datasetsById = useSelector((state: RootState) => datasetsSelectors.getDatasetsById(state));
  const transactionsByAccountId = useSelector((state: RootState) => getTransactionsByAccountId(state, undefined));
  const featureFlags = useSelector((state: RootState) => featureFlagsSelectors.getFeatureFlags(state));
  const accountColorForId = useSelector((state: RootState) => accountColorForIdSelector(state, txn.accountId!));
  const scheduledTransactionsById = useSelector((state: RootState) => scheduledTransactionsSelectors.getScheduledTransactions(state));
  const hasWriteAccess = useSelector((state: RootState) => hasDatasetWriteAccess(state));
  const isTransferAccountPresent = useSelector((state: RootState) => isTransferWritableAccountAvailable(state, txn));
  
  const dataset = datasetsById.get(datasetId!);
  const tagsAsHashes = featureFlags.get('tagsAsHashes');
  const noDateOnBankPending = featureFlags.get('noDateOnBankPending');
  const txnCancelExtraColumn = featureFlags.get('txnCancelExtraColumn');
  const labelPendingAsFuture = featureFlags.get('labelPendingAsFuture');
  const suggestCategoryForUncategorized = featureFlags.get('suggestCategoryForUncategorized');
  
  const selected = Boolean(selectedTxns?.find((selectedTxn: transactionsTypes.CashFlowTransaction) => selectedTxn.id === props.txn.id));

  const [showIcons, setShowIcons] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false); 
  const [accountMenuEl, setAccountMenuEl] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState(getCurrencySymbol({ showCurrencySymbol, txn, getTxnCurrency }));
  
  const focusField = useRef(null);
  const txnRef = useRef(txn);
  const prevTxn = useRef(txn);
  const prevEditable = useRef(editable);
  const prevShowCurrencySymbol = usePrevious(showCurrencySymbol);
  const isTypeGrossPaycheck = useRef(transactionsUtils.isTxnGrossPaycheckType(txn, transactionsByAccountId, scheduledTransactionsById));
  const isC2REnabled = isC2REnabledAccount(txn.accountId);
  const isQueuePmt = useMemo(() => QUEUE_PAYMENTS.includes(txn.check?.number as string), [txn.check]);
  const isEditingQueuePmt = Boolean(txnRef.current?.id && isQueuePmt);
  const hasEditAccess = hasWriteAccess && isTransferAccountPresent;

  useRefChangeWatcher(focusField, (newFocusField) => {
    if (onFocusField && newFocusField) {
      onFocusField(newFocusField);
    }
  });

  useEffect(() => {
    txnRef.current = txn;
  }, [txn]);

  const isBankPending = isBankPendingTxn(txn);
  const isWindowsDataset = datasetsUtils.isWindowsDataset(dataset!);

  const updateShowTooltip = (flag) => {
    setShowTooltip(flag);
  };

  useEffect(() => {
    if ((prevShowCurrencySymbol !== showCurrencySymbol) || txn !== prevTxn.current) {
      setCurrencySymbol(getCurrencySymbol({ showCurrencySymbol, txn, getTxnCurrency }));
    }
    if (editable !== prevEditable.current) {
      prevTxn.current = txn;
    }

  }, [prevShowCurrencySymbol, showCurrencySymbol, txn, prevTxn, getTxnCurrency, editable, prevEditable]);

  useEffect(() => {
    if (showTooltip) {
      setTimeout(() => {
        updateShowTooltip(false);
      }, 3000);
    }
  }, [showTooltip]);

  const fieldIsEditableMethod = (fieldPassed): boolean => {

    const options = {
      accountIds,
      editRunningBalance: false,
      scheduledTransactionsById,
      editDownloaded,
      txnCancelExtraColumn,
      showAccountColors,
      headerFields,
      regEditableFields,
      showSplitWindow,
      allAccountIds,
    };

    if (!isUnacceptedScheduledTxn(txn)) {
      return fieldIsEditable(txn, fieldPassed, options);
    }
    return !!isNextScheduledInstance(txn, scheduledTransactionsById) && fieldIsEditable(txn, fieldPassed, options);
  };

  const itemKeydown = (e, fieldPassed, txnPassed) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.stopPropagation();
      callbacks.regFieldClick?.(fieldPassed, txnPassed);
    }
  };

  const updateAccountMenuEl = (menuEl) => {
    setAccountMenuEl(menuEl);
  };

  const renderFields = ({ showSplitsAndDetails, showColor }) => {
    // TODO: put back these currently unused props (was getting lint issues), when showAccounts is used again
    // scheduledTransactionsById, usePayeeField, payeeAutoFillCat, payeeAutoFillMemo, payeeAutoFillAmount, 
    // payeeAutoFillTags, payeeShowFillInfoInMenu, sortBy

    const enableAutoFocus = true;
    const amountProposedSign = (!txn.id || Number(txn.amount) === 0) ? txnCategorySign(txn) : null;
    const wrapClass = wrapText ? 'wraptext' : '';

    const txnRow: React.JSX.Element[] = [];

    let isEditable = true;
    if (isTypeGrossPaycheck.current) {
      isEditable = false;
    } else {
      isEditable = !isBankOwnedPending(txn) ? (editable && hasEditAccess) : (editable && !isC2REnabled);
    }

    // Condition for account not synced case
    let isAccountNotSynced = false;
    if (txn.id && txn?.coa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT && !allAccountIds.includes(txn.coa.id)) {
      isEditable = false;
      isAccountNotSynced = true;
    }

    let bankPendingClass = '';
    if (isBankPending) {
      bankPendingClass = isBankOwnedPending(txn) ? classes.bankOwnedPendingText : classes.bankPendingText;
    }

    if ((!isTxnDirty || showNewTxn) && txn.id && isQueuePmt) {
      isEditable = false;
    }

    headerFields.forEach((field) => {

      const forceStatic = isEditable && !fieldIsEditableMethod(field);
      const fieldActive = (isEditable && !forceStatic && currEditFieldName === field);
      let staticValue: React.JSX.Element | string | undefined | null = (<span>{'\u00A0'}</span>);

      const fieldKey = `${scriptId}_${field}_${fieldActive ? 'e' : ''}`;
      const editClass = fieldActive ? 'editable' : '';
      const hoverClass = (fieldActive || !fieldIsEditableMethod(field)) ? '' : classes.editHover;

      const attributes: AttributesType = {
        key: fieldKey,
        id: fieldKey,
        tabIndex: '0',
        onClick: () => callbacks.regFieldClick?.(field, txn),
        onKeyDown: (e) => !isEditable && itemKeydown(e, field, txn),
      };

      switch (field) {

        case 'action':
          txnRow.push(
            <ActionField 
              attributes={attributes}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              editClass={editClass}
              hoverClass={hoverClass}
            />,
          );
          break;

        case 'security':
          txnRow.push(
            <RegListItem
              {...attributes}
              tabIndex={null}
              onClick={null}
              className={classNames(classes.regItem, 'accountFieldName', 'protectWidth', wrapClass, editClass, hoverClass)}
            >
              {SHOW_ALL && <div>{txn.symbol}{txn.symbol && ' / '}{txn.payee}</div>}
            </RegListItem>,
          );
          break;

        case 'quantity':
          txnRow.push(
            <RegListItem
              {...attributes}
              tabIndex={null}
              onClick={null}
              className={classNames('amount', classes.regItem, 'quantity', editClass, hoverClass)}
            >
              {SHOW_ALL && <div>{txn.units || '-'}</div>}
            </RegListItem>,
          );
          break;

        case 'price':
          txnRow.push(
            <RegListItem
              {...attributes}
              tabIndex={null}
              onClick={null}
              className={classNames('amount', classes.regItem, 'protectWidth', editClass, hoverClass)}
            >
              {SHOW_ALL && <div>{txn.costBasis || '-'}</div>}
            </RegListItem>,
          );
          break;

        // ------------------------------------------------
        // MATCH FIELD
        // ------------------------------------------------
        case 'match':
          txnRow.push(
            <MatchField
              attributes={attributes}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
            />,
          );
          break;

        // ------------------------------------------------
        // REVIEWED FIELD
        // ------------------------------------------------
        case 'reviewed':
          txnRow.push(
            <ReviewedField 
              attributes={attributes}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              forceStatic={forceStatic}
              callbacks={callbacks}
              isWindowsDataset={isWindowsDataset}
              fieldActive={fieldActive}
              isTypeGrossPaycheck={isTypeGrossPaycheck.current}
              showTooltip={showTooltip}
              updateShowTooltip={updateShowTooltip}
              scriptId={scriptId}
              field={field}
              isAccountNotSynced={isAccountNotSynced}
              editable={isEditable}
              canReview={hasWriteAccess}
            />,
          );
          break;

        // ------------------------------------------------
        // FLAG FIELD
        // ------------------------------------------------
        case 'userFlag':
          txnRow.push(
            <UserFlagField 
              attributes={attributes}
              callbacks={callbacks}
              editable={isEditable}
              fieldActive={fieldActive}
              txn={txn}
              showIcons={showIcons}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // IGNORED FIELD
        // ------------------------------------------------
        case 'ignored':
          txnRow.push(
            <IgnoredField 
              attributes={attributes}
              callbacks={callbacks}
              editable={isEditable}
              fieldActive={fieldActive}
              txn={txn}
              showIcons={showIcons}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // SELECT FIELD or RECURRING ICON FIELD
        // ------------------------------------------------
        case 'select':
          txnRow.push(
            <SelectField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              currEditFieldName={currEditFieldName}
              isAccountNotSynced={isAccountNotSynced}
              isTypeGrossPaycheck={isTypeGrossPaycheck.current}
              selected={selected}
              onRowSelect={onRowSelect}
              msgGrossPaycheck={msgGrossPaycheck}
              focusField={focusField}
              isTxnDirty={isTxnDirty}
              showNewTxn={showNewTxn}
            />,
          );
          break;

        // ------------------------------------------------
        // ACCOUNT COLOR FIELD
        // ------------------------------------------------
        case 'accountColor': {

          if (showAccountColors) {
            txnRow.push(
              <AccountField 
                attributes={attributes}
                forceStatic={forceStatic}
                callbacks={callbacks}
                SHOW_ALL={SHOW_ALL}
                fieldActive={fieldActive}
                txn={txn}
                wrapClass={wrapClass}
                showAccountColors={showAccountColors}
                accountColorForId={accountColorForId}
                accountMenuEl={accountMenuEl}
                updateAccountMenuEl={updateAccountMenuEl}
                editable={isEditable}
                focusField={focusField}
                bankPendingClass={bankPendingClass}
              />,
            );
          }
          break;
        }

        // ------------------------------------------------
        // ACCOUNT NAME FIELD
        // ------------------------------------------------
        case 'account': {

          if (showAccounts && !showAccountColors) {
            txnRow.push(
              <AccountField 
                attributes={attributes}
                forceStatic={forceStatic}
                callbacks={callbacks}
                SHOW_ALL={SHOW_ALL}
                fieldActive={fieldActive}
                txn={txn}
                wrapClass={wrapClass}
                showAccountColors={showAccountColors}
                accountColorForId={accountColorForId}
                accountMenuEl={accountMenuEl}
                updateAccountMenuEl={updateAccountMenuEl}
                editable={isEditable}
                focusField={focusField}
                bankPendingClass={bankPendingClass}
              />,
            );
          }
          break;
        }

        // ------------------------------------------------
        // POSTED_ON FIELD
        // ------------------------------------------------
        case 'postedOn': {
          txnRow.push(
            <PostedOnField 
              callbacks={callbacks}
              attributes={attributes}
              forceStatic={forceStatic}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              noDateOnBankPending={noDateOnBankPending}
              editClass={editClass}
              hoverClass={hoverClass}
              currEditFieldName={currEditFieldName}
              enableAutoFocus={enableAutoFocus}
              showColor={showColor}
              bankPendingClass={bankPendingClass}
            />,
          );
          break;
        }
        // ------------------------------------------------
        // TAGS FIELD
        // ------------------------------------------------
        case 'tags': {  
          txnRow.push(
            <TagsField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              field={field}
              enableAutoFocus={enableAutoFocus}
              showSplitWindow={showSplitWindow}
              staticValue={staticValue}
              wrapClass={wrapClass} 
              editClass={editClass} 
              hoverClass={hoverClass}
              wrapText={wrapText}
              tagsAsHashes={tagsAsHashes}
              focusField={focusField}
            />,
          );
          break;
        }

        // ------------------------------------------------
        // NOTES FIELD
        // ------------------------------------------------
        case 'notes':

          txnRow.push(
            <NotesField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              wrapClass={wrapClass}
              editClass={editClass}
              hoverClass={hoverClass}
              currEditFieldName={currEditFieldName}
              enableAutoFocus={enableAutoFocus}
              wrapText={wrapText}
              field={field}
              staticValue={staticValue}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // CHECK FIELD
        // ------------------------------------------------
        case 'check':

          if (txn.check && (txn.check.number || txn.check.memo)) {
            staticValue = txn.check.number || txn.check.memo;
          }

          txnRow.push(
            <CheckField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              wrapClass={wrapClass} 
              editClass={editClass} 
              hoverClass={hoverClass}
              currEditFieldName={currEditFieldName}
              enableAutoFocus={enableAutoFocus}
              staticValue={staticValue}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // PAYEE FIELD
        // ------------------------------------------------
        case 'payee':

          txnRow.push(
            <PayeeField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              wrapClass={wrapClass} 
              editClass={editClass} 
              hoverClass={hoverClass} 
              bankPendingClass={bankPendingClass}
              currEditFieldName={currEditFieldName}
              enableAutoFocus={enableAutoFocus}
              accountIds={accountIds}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // CATEGORY FIELD
        // ------------------------------------------------
        case 'category':

          txnRow.push(
            <CategoryField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              txn={txn}
              wrapClass={wrapClass}
              editClass={editClass} 
              hoverClass={hoverClass}
              bankPendingClass={bankPendingClass}
              currEditFieldName={currEditFieldName}
              wrapperId={wrapperId}
              longCats={longCats}
              wrapText={wrapText}
              showSplitWindow={showSplitWindow}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // SPLIT FIELD
        // ------------------------------------------------
        case 'split': {
          txnRow.push(
            <SplitField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              payeeCategoryMap={payeeCategoryMap}
              suggestCategoryForUncategorized={suggestCategoryForUncategorized}
              focusField={focusField}
            />,
          );
          break;
        }

        // ------------------------------------------------
        // STATE FIELD
        // ------------------------------------------------
        case 'state': {

          txnRow.push(
            <RegListItem
              {...attributes}
              className={classNames(classes.statusColumn, !isC2REnabled ? hoverClass : '')}
              onClick={null}
            >
              {SHOW_ALL && !(isBankOwnedPending(txn) && isC2REnabled) &&
              <ClrField
                value={txn.state!}
                // @ts-expect-error: className not in ClrFieldProps but passed through
                className={classes.select}
                onChange={(e) => callbacks.handleMenu?.('state', txn, e.target.value)}
                editable={hasEditAccess && fieldIsEditableMethod(field) && !isTypeGrossPaycheck.current && !isEditingQueuePmt}
                autoFocus={fieldActive}
              />}
            </RegListItem>,
          );
          break;
        }

        // ------------------------------------------------
        // ATTACHMENT FIELD
        // ------------------------------------------------
        case 'attachment':
          txnRow.push(
            <AttachmentField 
              attributes={attributes}
              callbacks={callbacks}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // EXPENSE FIELD
        // ------------------------------------------------
        case 'expense':
          if (doubleAmounts) {
            txnRow.push(
              <DoubleAmountField 
                field={'expense'}
                attributes={attributes}
                callbacks={callbacks}
                editable={isEditable}
                SHOW_ALL={SHOW_ALL}
                txn={txn}
                currencySymbol={currencySymbol} // state
                amountEditable={fieldIsEditableMethod(field)}
                currEditFieldName={currEditFieldName}
                enableAutoFocus={enableAutoFocus}
                focusField={focusField}
              />,
            );
          }
          break;

        // ------------------------------------------------
        // INCOME FIELD
        // ------------------------------------------------
        case 'income':
          if (doubleAmounts) {
            txnRow.push(
              <DoubleAmountField 
                field={'income'}
                attributes={attributes}
                callbacks={callbacks}
                editable={isEditable}
                SHOW_ALL={SHOW_ALL}
                txn={txn}
                currencySymbol={currencySymbol} // state
                amountEditable={fieldIsEditableMethod(field)}
                currEditFieldName={currEditFieldName}
                enableAutoFocus={enableAutoFocus}
                focusField={focusField}
              />,
            );
          }
          break;

        // ------------------------------------------------
        // AMOUNT FIELD
        // ------------------------------------------------
        case 'amount':
          if (!doubleAmounts) {
            txnRow.push(
              <TxnRowAmountField
                attributes={attributes}
                callbacks={callbacks}
                editable={isEditable}
                SHOW_ALL={SHOW_ALL}
                txn={txn}
                editClass={editClass} 
                hoverClass={hoverClass} 
                bankPendingClass={bankPendingClass}
                field={field}
                fieldIsEditable={fieldIsEditableMethod}
                currEditFieldName={currEditFieldName}
                currencySymbol={currencySymbol}
                enableAutoFocus={enableAutoFocus}
                amountProposedSign={amountProposedSign}
                focusField={focusField}
              />,
            );
          }
          break;

        // ------------------------------------------------
        // BALANCE FIELD
        // ------------------------------------------------
        case 'balance':
          if (showBalance) {
            txnRow.push(
              <BalanceField 
                attributes={attributes}
                callbacks={callbacks}
                SHOW_ALL={SHOW_ALL}
                txn={txn}
                field={field}
                lastRow={lastRow}
                enableAutoFocus={enableAutoFocus}
                currencySymbol={currencySymbol}
                showColor={showColor}
                hoverClass={hoverClass}
                bankPendingClass={bankPendingClass}
                isWindowsDataset={isWindowsDataset}
                editable={hasWriteAccess}
              />,
            );
          }
          break;

        // ------------------------------------------------
        // MENU FIELD
        // ------------------------------------------------
        case 'menu': {
          txnRow.push(
            <MenuField 
              attributes={attributes}
              callbacks={callbacks}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              showSplitsAndDetails={showSplitsAndDetails}
              field={field}
              fieldIsEditable={fieldIsEditableMethod}
              focusField={focusField}
            />,
          );
          break;
        }

        // ------------------------------------------------
        // SAVE FIELD
        // ------------------------------------------------
        case 'save':
          txnRow.push(
            <SaveField 
              attributes={attributes}
              forceStatic={forceStatic}
              callbacks={callbacks}
              editable={isEditable}
              SHOW_ALL={SHOW_ALL}
              fieldActive={fieldActive}
              txn={txn}
              focusField={focusField}
            />,
          );
          break;

        // ------------------------------------------------
        // CLOSE FIELD
        // ------------------------------------------------
        case 'close':
          if (txnCancelExtraColumn) {
            txnRow.push(
              <CloseField 
                attributes={attributes}
                forceStatic={forceStatic}
                callbacks={callbacks}
                editable={isEditable}
                SHOW_ALL={SHOW_ALL}
                fieldActive={fieldActive}
                focusField={focusField}
              />,
            );
          }
          break;


        default:
          log.error(`Bad Field ${field} sent to TransactionRow`);
          break;
      }
    });

    return txnRow;
  };

  const displayTransactionModal = () => {
    if (isTypeGrossPaycheck.current) {
      dialogAlert(
        'Notification',
        msgGrossPaycheck,
        noop,
        ['Ok'],
      );
    }
  };

  //
  // This processes the "SAVE" call from the details panels (splits and/or edit details)
  // We give each of those a copy of the txnBeingEdited so that cancel is easy enough, but
  // it is possible that the user can edit the txnBeingEdited from the transaction row while
  // splits and details are open, which can lead to the transaction fields being edited from
  // two places at once.  We need to a) not lose any edited data from the transaction row, and
  // b) resolve conflicts if the data is edited both places.  In this case, the main txn field
  // wins.
  //
  const saveTransaction = (editedTxn, autoSave, txnDirty, splitToggle = undefined) => {
    const txnBeingEdited = txnRef.current;
    const newTxn = editedTxn;
    let localDirtyFlag = false;
    const txnRowChangedProps = getTxnDifferences(txnBeingEdited, prevTxn.current);
    const detailsPanelChangedProps = getTxnDifferences(txnBeingEdited, newTxn);
    // if user changed memo, tags, payee, coa, postedOn, or check number, then we use the
    // version from the main transaction instead of the details panel.  Exception, if tx is split
    // we ignore the apparent coa change
    //
    const txnPropsChanged = txnRowChangedProps.filter((changedProp) => !detailsPanelChangedProps.includes(changedProp));
    txnPropsChanged.forEach((fieldPassed) => {
      switch (fieldPassed) {
        case 'postedOn':
        case 'memo':
        case 'check':
        case 'state':
        case 'payee':
          newTxn[fieldPassed] = txnBeingEdited[fieldPassed];
          localDirtyFlag = true;
          break;

        case 'split':
          localDirtyFlag = true;
          break;

        case 'amount':
        case 'tags':
        case 'coa':
          if (!transactionsUtils.isSplitTxn(newTxn)) {
            newTxn[fieldPassed] = txnBeingEdited[fieldPassed] || newTxn[fieldPassed];  // don't ever purposefully assign null
            localDirtyFlag = true;
          }
          break;

        default:
          break;
      }
    });
    callbacks.saveTransaction?.(newTxn, autoSave, txnDirty || localDirtyFlag, splitToggle);
  };

  const panelOpen = (showSplitWindow || !simpleEdit) && editable;
  const showColor = !calendar;

  /* eslint-disable prefer-template */
  const className = (shade ? 'shade' : '') +
    (lastRow ? ' lastRow appear' : '') +
    (panelOpen ? ' panel' : '') +
    (editable ? ' editable appear' : '') +
    (` ${registerComfort}${isIe ? '-ie' : ''}`) +
    (DateTime.fromISO(txn.postedOn) > (DateTime.now()) && !labelPendingAsFuture && showColor ? ' pending' : '') +
    (isUnacceptedScheduledTxn(txn) && showColor ? ' pending' : '') +
    (getColorClass(!showColor, txn.amount));

  const showSplitsAndDetails = editable && (showSplitWindow || !simpleEdit);
  const fieldsToRender = renderFields({ showSplitsAndDetails, showColor });
  
  let isSplitsEditable = true;
  if (isTypeGrossPaycheck.current) {
    isSplitsEditable = false;
  } else {
    isSplitsEditable = isBankOwnedPending(txn) ? !isC2REnabled : true;
  }

  if ((!isTxnDirty || showNewTxn) && txn.id && isQueuePmt) {
    isSplitsEditable = false;
  }

  return (
    <RegTxList
      ref={refFn}
      aria-label={scriptId}
      id={id}
      key={`${txn.id}`}
      className={className}
      onMouseEnter={() => setShowIcons(true)}
      onMouseLeave={() => setShowIcons(false)}
    >
      <div {...(qcardId ? { id: qcardId } : {})}>
        {/* --------------------------------------------------- */}
        {/* -- ALL ROW FIELDS -- */}
        {/* --------------------------------------------------- */}
        <RegTxListRow
          className={classNames(flashTx ? 'flashTx' : '', className)}
          onClick={isTypeGrossPaycheck.current ? displayTransactionModal : undefined}
        >
          {fieldsToRender}
          <Dump obj={txn} />
        </RegTxListRow>

        {/* --------------------------------------------------- */}
        {/* -- SPLITS/DETAILS PANEL -- */}
        {/* --------------------------------------------------- */}
        {showSplitsAndDetails &&
          <SplitsAndDetailsPanel
            autoFocus
            txn={txn}
            closeFn={() => callbacks.hideModal?.('splits')}
            saveFn={saveTransaction}
            deleteFn={() => callbacks.handleMenu?.('delete', txn)}
            onAmountChange={callbacks.onAmountChange}
            longCats={longCats}
            showCurrencySymbol={showCurrencySymbol}
            onSizeChange={sizesChanged}
            showDetails={!simpleEdit}
            showSplits={showSplitWindow}
            unMatchAction={() => callbacks.handleMenu?.('unMatch', txn)}
            goToTransferAction={() => callbacks.handleMenu?.('transfer', txn)}
            getSaveFn={callbacks.detailsRefSaveFn}
            splitSwitch={(on) => on ? callbacks.handleMenu?.('split', txn) : callbacks.handleMenu?.('hideSplit', txn)}
            editable={hasEditAccess && isSplitsEditable}
          />}
      </div>
    </RegTxList>
  );
};

export default memo(TransactionRow);
